/**
 * LiveSessionPage – the heart of LingoFlow.
 *
 * Turn-based voice conversation loop:
 *
 *   1. User taps the orb → MediaRecorder starts.
 *   2. User taps again → recording stops, blob POSTs to /api/stt (Whisper).
 *   3. Transcript text POSTs to /api/turn → GPT-5.2 replies.
 *   4. Reply text POSTs to /api/tts → MP3 is streamed back and auto-played.
 *   5. Transcript bubbles are appended and the view scrolls.
 *
 * Quick action buttons (Repeat / Slow down / Translate / Help me answer)
 * send a synthetic user turn with the relevant flag so the tutor adapts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CaretLeft,
  Microphone,
  Stop,
  Repeat,
  SpeakerLow,
  Translate,
  Lightbulb,
  FlagCheckered,
  SpinnerGap,
  Play,
  ArrowSquareOut,
  WarningCircle,
  Question,
  GraduationCap,
} from '@phosphor-icons/react';

import { http, API, apiErrorMessage, getStoredToken } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isNative } from '@/lib/native';
import useRecorder from '@/hooks/useRecorder';
import BreathingOrb from '@/components/BreathingOrb';
import TappableText from '@/components/TappableText';
import WordDefinitionSheet from '@/components/WordDefinitionSheet';
import LessonPlanCard from '@/components/LessonPlanCard';
import AskTeacherSheet from '@/components/AskTeacherSheet';
import MiniDrillModal from '@/components/MiniDrillModal';
import MiniQuizModal from '@/components/MiniQuizModal';

export default function LiveSessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [turns, setTurns] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | recording | transcribing | thinking | speaking
  const [error, setError] = useState('');
  const [lastAiTurn, setLastAiTurn] = useState(null);
  const [ending, setEnding] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [vadEnabled, setVadEnabled] = useState(true);

  // Teacher-Mode state. A session is "teacher mode" if it was started via
  // POST /api/teacher/start — which attaches teacher_mode=True + lesson_plan
  // to the session document. The learner can also toggle it globally in Profile.
  const [teacherMode, setTeacherMode] = useState(false);
  const [lessonPlan, setLessonPlan] = useState(null);
  const [askOpen, setAskOpen] = useState(false);
  const [drillFocus, setDrillFocus] = useState(null);   // active mini-drill
  const [quizOpen, setQuizOpen] = useState(false);
  // Phase 2 — Prep words the learner flagged on the PrepScreen. Mirrored from
  // the session doc on load and flipped to `used: true` as /api/turn reports
  // `prep_used_hits`.
  const [prepWords, setPrepWords] = useState([]);

  const transcriptRef = useRef(null);
  const audioRef = useRef(null);
  const sessionRef = useRef(null);
  const statusRef = useRef('idle');
  // Phase 4 — latency_ms for the adaptive engine. Set when the AI finishes
  // speaking; consumed when the user starts recording. Difference =
  // "thinking time" before answering.
  const aiEndedAtRef = useRef(0);
  const lastLatencyMsRef = useRef(0);
  useEffect(() => { statusRef.current = status; }, [status]);

  // When VAD fires, finalize recording and send the transcript.
  // `finalizeRecording` is defined later in the component body (hoisted via
  // function declaration). We intentionally don't depend on it here — the
  // setTimeout(0) defers to the next tick where the up-to-date closure is used.
  const handleAutoStop = useCallback(() => {
    setTimeout(() => finalizeRecording().catch(() => {}), 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const recorder = useRecorder({ vad: vadEnabled, onAutoStop: handleAutoStop });

  // Word lookup sheet state
  const [defWord, setDefWord] = useState(null);   // { term, context }
  function openDef(term, context) { setDefWord({ term, context }); }
  function closeDef() { setDefWord(null); }

  // ISO code hint for Whisper + TTS based on user's practice direction
  const targetLang = (user?.practice_direction === 'es_to_en') ? 'en' : 'es';

  // Load session + existing turns
  useEffect(() => {
    (async () => {
      try {
        const [{ data: t }, { data: state }] = await Promise.all([
          http.get(`/session/${sessionId}/turns`),
          http.get(`/session/${sessionId}/state`),
        ]);
        setTurns(t);
        sessionRef.current = state;
        if (state?.teacher_mode) {
          setTeacherMode(true);
          setLessonPlan(state.lesson_plan || null);
        }
        if (state?.prep_words?.length) {
          setPrepWords(state.prep_words);
        }
        if (state?.review_ready) navigate(`/review/${sessionId}`, { replace: true });
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
  }, [sessionId, navigate]);

  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [turns]);

  // Greet the user with an opening AI line if the session is brand-new.
  // Guard against StrictMode double-invocation with a ref.
  const greetedRef = useRef(false);
  useEffect(() => {
    if (greetedRef.current) return;
    if (turns.length === 0) {
      greetedRef.current = true;
      const greet = targetLang === 'es' ? 'Hola, ¿cómo estás?' : 'Hi, how are you?';
      sendUserText(greet, {}, /* isGreeting */ true).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Play TTS for a given text. Returns a promise that resolves when audio ends. */
  const speak = useCallback(async (text) => {
    if (!text) return;
    try {
      setStatus('speaking');
      const resp = await fetch(`${API}/tts`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),  // voice + speed come from user prefs
      });
      if (!resp.ok) throw new Error('TTS failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      await new Promise((resolve) => {
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.playbackRate = playbackRate;
        audio.onended = () => {
          URL.revokeObjectURL(url);
          // Phase 4 — start the latency clock the moment the AI stops.
          aiEndedAtRef.current = Date.now();
          resolve();
        };
        audio.onerror = () => { URL.revokeObjectURL(url); resolve(); };
        audio.play().catch(() => resolve());
      });
    } catch (_) {
      // Non-fatal: we still have the text on screen.
    } finally {
      setStatus('idle');
    }
  }, [playbackRate]);

  /** Replay the last AI line. */
  const replayLast = useCallback(() => {
    const lastAi = [...turns].reverse().find((t) => t.speaker === 'ai');
    if (lastAi) speak(lastAi.text);
  }, [turns, speak]);

  async function sendUserText(text, flags = {}, isGreeting = false) {
    if (!text?.trim()) return;
    setError('');
    const isAction = !!(flags.repeat || flags.translate || flags.help || flags.slow);
    // Optimistic append of user bubble (unless it's the silent greeting or action)
    const tempUserId = `tmp-${Date.now()}`;
    if (!isGreeting && !isAction) {
      setTurns((prev) => [...prev, {
        id: tempUserId, session_id: sessionId, speaker: 'user', text,
        timestamp: new Date().toISOString(),
      }]);
    }
    setStatus('thinking');
    try {
      const { data } = await http.post('/turn', {
        session_id: sessionId,
        user_text: text,
        help_requested: !!flags.help,
        request_slow: !!flags.slow,
        request_repeat: !!flags.repeat,
        request_translate: !!flags.translate,
        is_greeting: isGreeting,
        is_action: isAction,
        latency_ms: lastLatencyMsRef.current || 0,
      });
      // Reset latency tracker after consumption.
      lastLatencyMsRef.current = 0;
      const ai = data.ai_turn;
      setLastAiTurn({ ...ai, native_helper: data.reply_native_helper, gentle_note: data.gentle_note });

      // Teacher Mode: update lesson plan progress + optionally fire a mini-drill.
      if (teacherMode) {
        const hits = data.lesson_progress_hits || [];
        if (hits.length && lessonPlan?.items?.length) {
          setLessonPlan((prev) => {
            if (!prev) return prev;
            const next = { ...prev, items: prev.items.map((it) => (
              hits.map((h) => h.toLowerCase()).includes(it.target.toLowerCase())
                ? { ...it, used: true } : it
            )) };
            return next;
          });
        }
        // Defer mini-drill until TTS finishes — much less jarring.
        const focus = data.mini_drill_focus || '';
        if (focus) {
          setTimeout(() => setDrillFocus(focus), 150);
        }
      }

      // Phase 2 — Prep words: flip `used` on any lemma the backend reports
      // as used in this turn. The hit list comes from /api/turn's JSON.
      const prepHits = data.prep_used_hits || [];
      if (prepHits.length && prepWords.length) {
        const lower = prepHits.map((h) => h.toLowerCase());
        setPrepWords((prev) => prev.map((p) => (
          lower.includes((p.lemma || '').toLowerCase()) ? { ...p, used: true } : p
        )));
      }

      if (isGreeting || isAction) {
        setTurns((prev) => [...prev, ai]);
      } else {
        setTurns((prev) => [...prev.filter((t) => t.id !== tempUserId), {
          id: `u-${ai.id}`, session_id: sessionId, speaker: 'user', text,
          timestamp: new Date().toISOString(),
        }, ai]);
      }
      await speak(ai.text);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && typeof detail === 'object') {
        setError(detail.message || 'Upgrade to continue.');
        // Pass the specific cap kind so /pricing can tailor the banner.
        const reason = detail.kind || 'free_daily_cap';
        setTimeout(() => navigate(`/pricing?reason=${reason}`), 1000);
        return;
      }
      setError(apiErrorMessage(e));
      setStatus('idle');
    }
  }

  async function finalizeRecording() {
    // Guard against double-fire (user tap + VAD racing).
    if (statusRef.current === 'transcribing' || statusRef.current === 'thinking') return;
    setStatus('transcribing');
    const blob = await recorder.stop();
    if (!blob || blob.size < 500) {
      setStatus('idle');
      return;
    }
    try {
      const form = new FormData();
      form.append('audio', blob, 'clip.webm');
      form.append('language', targetLang);
      const resp = await fetch(`${API}/stt?language=${targetLang}`, {
        method: 'POST',
        credentials: 'include',
        headers: getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {},
        body: form,
      });
      const j = await resp.json();
      const text = (j?.text || '').trim();
      if (!text) { setStatus('idle'); return; }
      await sendUserText(text);
    } catch (e) {
      setError(e.message || 'Transcription failed');
      setStatus('idle');
    }
  }

  // Detect if we're running inside an iframe — browsers block mic access from
  // iframes unless the parent explicitly allows it, which is exactly what's
  // happening in the Emergent preview chat view. We surface a clear banner
  // + "Open in new tab" CTA so the user doesn't get stuck. On native we
  // always skip this — Capacitor handles permissions directly.
  const inIframe = (() => {
    if (isNative()) return false;
    try { return window.self !== window.top; } catch { return true; }
  })();

  function openInNewTab() {
    try { window.open(window.location.href, '_blank', 'noopener,noreferrer'); }
    catch { /* popup blocked — user will have to copy the URL */ }
  }

  async function toggleMic() {
    if (status === 'thinking' || status === 'transcribing') return;

    // If AI is speaking and user taps mic, stop playback and start listening.
    if (status === 'speaking') {
      try { audioRef.current?.pause(); } catch { /* audio already ended — safe to ignore */ }
      setStatus('idle');
    }

    if (!recorder.recording) {
      // Guard: no mediaDevices (very old browsers / http / iframe w/o perms)
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(
          inIframe
            ? 'Microphone is blocked inside this preview window. Tap "Open in new tab" above to enable voice.'
            : 'This browser doesn\'t support microphone access. Try the latest Chrome, Edge, or Safari.'
        );
        return;
      }
      try {
        await recorder.start();
        // Phase 4 — capture latency from "AI stopped speaking" to "user
        // started recording". Lower = more confident/quicker reply.
        if (aiEndedAtRef.current > 0) {
          lastLatencyMsRef.current = Math.max(0, Date.now() - aiEndedAtRef.current);
        } else {
          lastLatencyMsRef.current = 0;
        }
        setStatus('recording');
      } catch (e) {
        const name = e?.name || '';
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          setError(
            inIframe
              ? 'Your browser blocked the mic prompt because this app is inside a preview frame. Tap "Open in new tab" above and try again.'
              : 'Microphone permission was denied. Click the 🔒 icon in the address bar → Site settings → Allow microphone.'
          );
        } else if (name === 'NotFoundError') {
          setError('No microphone found on this device.');
        } else {
          setError(e?.message || 'Could not start microphone.');
        }
      }
      return;
    }

    // User manually stops — same pipeline as VAD auto-stop.
    await finalizeRecording();
  }

  async function endSession() {
    setEnding(true);
    try {
      await http.post(`/session/${sessionId}/end`);
      // In Teacher Mode, show the 3-card mini-quiz BEFORE jumping to Review.
      if (teacherMode && lessonPlan?.items?.length) {
        setEnding(false);
        setQuizOpen(true);
        return;
      }
      navigate(`/review/${sessionId}`, { replace: true });
    } catch (e) {
      setError(apiErrorMessage(e));
      setEnding(false);
    }
  }

  function handleQuizClose() {
    setQuizOpen(false);
    navigate(`/review/${sessionId}`, { replace: true });
  }

  const orbState = useMemo(() => {
    if (status === 'recording') return 'listening';
    if (status === 'speaking') return 'speaking';
    if (status === 'thinking' || status === 'transcribing') return 'thinking';
    return 'idle';
  }, [status]);

  const orbLabel = {
    idle: 'Tap to speak',
    recording: recorder.voiceDetected ? 'Listening…' : 'I can hear you',
    transcribing: 'Got it',
    thinking: 'Thinking…',
    speaking: 'Speaking',
  }[status];

  const orbSubLabel = (() => {
    if (status !== 'recording') return undefined;
    if (!vadEnabled) return 'Tap to stop';
    if (!recorder.voiceDetected) return 'Start talking…';
    return 'Auto-stops when you pause';
  })();

  return (
    <div className="lf-shell flex flex-col h-screen" data-testid="session-page">
      {/* Iframe-warning banner — only shown if the app is embedded and the
          browser is almost certainly going to block the mic prompt. */}
      {inIframe ? (
        <div
          className="mx-4 mt-4 rounded-2xl border border-[var(--lf-accent)] bg-[#FFF6DB] px-4 py-3 text-sm"
          data-testid="iframe-mic-banner"
        >
          <div className="flex items-start gap-2">
            <WarningCircle size={18} weight="fill" className="mt-0.5 text-[#8A6B0F]" />
            <div className="flex-1 text-[#5C4809]">
              <div className="font-semibold">Your mic is blocked inside this preview</div>
              <div className="mt-0.5 text-[#6A5410]">
                Browsers block the microphone permission prompt in embedded windows.
                Open LingoFlow in its own tab to talk to the AI.
              </div>
              <button
                onClick={openInNewTab}
                className="lf-btn lf-btn-primary mt-3 w-full py-2 text-sm"
                data-testid="open-in-new-tab-btn"
              >
                <ArrowSquareOut size={14} weight="bold" /> Open in new tab
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-6">
        <button
          onClick={() => navigate('/home')}
          className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]"
          data-testid="session-back"
        >
          <CaretLeft size={18} />
        </button>
        <div className="text-center">
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
            {teacherMode ? 'Teacher session' : 'Live practice'}
          </div>
          <div className="text-sm font-semibold inline-flex items-center gap-1">
            {teacherMode ? <GraduationCap size={14} weight="duotone" className="text-[var(--lf-primary)]" /> : null}
            {targetLang === 'es' ? 'Spanish' : 'English'}
          </div>
        </div>
        <button
          onClick={endSession}
          disabled={ending}
          className="lf-chip lf-chip-primary"
          data-testid="session-end-btn"
        >
          {ending ? <SpinnerGap size={12} className="animate-spin" /> : <FlagCheckered size={12} weight="fill" />}
          End
        </button>
      </div>

      {/* Lesson Plan card — pinned when Teacher Mode is active. */}
      {teacherMode && lessonPlan?.items?.length ? (
        <LessonPlanCard plan={lessonPlan} onPlay={(t) => speak(t)} />
      ) : null}

      {/* Prep-words chip strip — shows focus words ticked off as they're used. */}
      {prepWords.length ? (
        <div
          className="mx-6 mb-2 rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface)] p-3"
          data-testid="session-prep-strip"
        >
          <div className="mb-1.5 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-[var(--lf-text-muted)]">
              Your focus words
            </div>
            <div className="text-[11px] font-semibold text-[var(--lf-primary)]" data-testid="session-prep-progress">
              {prepWords.filter((p) => p.used).length} / {prepWords.length}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {prepWords.map((p, i) => (
              <span
                key={p.lemma + i}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-all ${
                  p.used
                    ? 'bg-[var(--lf-primary)] text-white line-through opacity-90'
                    : 'bg-[var(--lf-surface-muted)] text-[var(--lf-text)] border border-[var(--lf-border)]'
                }`}
                data-testid={`session-prep-${i}${p.used ? '-used' : ''}`}
              >
                {p.lemma}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {/* Transcript */}
      <div
        ref={transcriptRef}
        className="lf-transcript flex-1 overflow-y-auto px-6 py-4 space-y-3"
        data-testid="session-transcript"
        role="log"
        aria-live="polite"
        aria-atomic="false"
        aria-label="Conversation transcript"
      >
        <AnimatePresence initial={false}>
          {turns.map((t) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${t.speaker === 'user' ? 'justify-end' : 'justify-start'}`}
              data-testid={`turn-${t.speaker}`}
            >
              <div className={t.speaker === 'user' ? 'lf-bubble-user' : 'lf-bubble-ai'}>
                <div className="text-sm leading-snug whitespace-pre-wrap">
                  {t.speaker === 'ai' ? (
                    <TappableText text={t.text} onWordTap={openDef} context={t.text} />
                  ) : t.text}
                </div>
                {t.translated_text ? (
                  <div className="mt-1 text-xs opacity-75 italic">{t.translated_text}</div>
                ) : null}
                {t.speaker === 'ai' ? (
                  <button
                    onClick={() => speak(t.text)}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--lf-secondary-foreground)] opacity-70 hover:opacity-100"
                    data-testid={`replay-${t.id}`}
                  >
                    <Play size={10} weight="fill" /> Play
                  </button>
                ) : null}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {status === 'thinking' && (
          <div className="flex justify-start">
            <div className="lf-bubble-ai flex items-center gap-2">
              <SpinnerGap size={14} className="animate-spin" /> thinking…
            </div>
          </div>
        )}
        {error ? <div className="text-sm text-[var(--lf-destructive)] text-center">{error}</div> : null}
      </div>

      {/* Quick actions */}
      <div className="px-6 pb-2 flex items-center gap-2 overflow-x-auto" data-testid="session-actions">
        <QuickAction icon={<Repeat size={14} weight="bold" />} label="Repeat" onClick={() => sendUserText('(repeat)', { repeat: true })} />
        <QuickAction
          icon={<SpeakerLow size={14} weight="bold" />}
          label={`Slow ${playbackRate < 1 ? '✓' : ''}`}
          onClick={() => {
            setPlaybackRate((r) => (r < 1 ? 1 : 0.8));
            replayLast();
          }}
        />
        <QuickAction icon={<Translate size={14} weight="bold" />} label="Translate" onClick={() => sendUserText('(translate)', { translate: true })} />
        <QuickAction icon={<Lightbulb size={14} weight="bold" />} label="Help me answer" onClick={() => sendUserText('(help)', { help: true })} />
        {teacherMode ? (
          <QuickAction
            icon={<Question size={14} weight="bold" />}
            label="Ask teacher"
            onClick={() => setAskOpen(true)}
          />
        ) : null}
      </div>

      {/* Mic area */}
      <div className="pb-8 pt-4 flex flex-col items-center" data-testid="session-mic-area">
        <button
          onClick={toggleMic}
          disabled={status === 'thinking' || status === 'transcribing' || ending}
          className="focus:outline-none"
          aria-label="Toggle microphone"
          data-testid="session-mic-btn"
        >
          <BreathingOrb
            state={orbState}
            size={200}
            level={recorder.level}
            label={orbLabel}
            sublabel={orbSubLabel}
          />
        </button>
        <div className="mt-3 flex items-center gap-3 text-xs text-[var(--lf-text-muted)]">
          {status === 'recording' ? (
            <>
              <Stop size={12} /> Recording
              <span className="mx-1 h-1 w-1 rounded-full bg-[var(--lf-text-muted)]/40" />
              <span>{vadEnabled ? 'Auto-stop on' : 'Auto-stop off'}</span>
            </>
          ) : status === 'idle' ? (
            <><Microphone size={12} /> Hold the idea, then tap</>
          ) : null}
        </div>
        <button
          onClick={() => setVadEnabled((v) => !v)}
          className="mt-2 text-[11px] font-semibold text-[var(--lf-text-muted)] hover:text-[var(--lf-primary)]"
          data-testid="session-vad-toggle"
        >
          {vadEnabled ? 'Turn off auto-stop' : 'Turn on auto-stop (recommended)'}
        </button>
      </div>

      <WordDefinitionSheet
        open={!!defWord}
        term={defWord?.term}
        context={defWord?.context}
        onClose={closeDef}
        practiceDirection={user?.practice_direction}
      />

      {/* Teacher Mode overlays */}
      <AskTeacherSheet
        open={askOpen}
        onClose={() => setAskOpen(false)}
        sessionId={sessionId}
        practiceDirection={user?.practice_direction}
      />
      <MiniDrillModal
        open={!!drillFocus}
        focus={drillFocus}
        language={targetLang}
        onClose={() => setDrillFocus(null)}
      />
      <MiniQuizModal
        open={quizOpen}
        sessionId={sessionId}
        onClose={handleQuizClose}
      />
    </div>
  );
}

function QuickAction({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 whitespace-nowrap rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)] px-3 py-1.5 text-xs font-semibold text-[var(--lf-text)] hover:bg-[var(--lf-surface-muted)] transition-colors"
      data-testid={`action-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {icon} {label}
    </button>
  );
}
